import { createSlice } from '@reduxjs/toolkit';

// Load timer state from localStorage
const loadTimerStateFromLocalStorage = () => {
  try {
    const savedTimers = localStorage.getItem('taskTimers');
    return savedTimers ? JSON.parse(savedTimers) : {};
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Failed to load task timers from localStorage:', err);
    return {};
  }
};

const initialState = {
  // Structure: { [activityId]: { timerActive, timerPaused, estimatedMinutes, timeRemainingSeconds, startTime } }
  timers: loadTimerStateFromLocalStorage(),
};

const taskTimerSlice = createSlice({
  name: 'taskTimer',
  initialState,
  reducers: {
    startTimer: (state, action) => {
      const { activityId, estimatedMinutes } = action.payload;
      const now = Date.now();
      state.timers[activityId] = {
        timerActive: true,
        timerPaused: false,
        estimatedMinutes,
        totalSeconds: estimatedMinutes * 60,
        startTime: now,
        lastUpdateTime: now,
        pausedAt: null,
        totalPausedDuration: 0, // Track total time spent paused
      };
      // Persist to localStorage
      localStorage.setItem('taskTimers', JSON.stringify(state.timers));
    },

    pauseTimer: (state, action) => {
      const { activityId } = action.payload;
      if (state.timers[activityId]) {
        state.timers[activityId].timerPaused = true;
        state.timers[activityId].pausedAt = Date.now();
        localStorage.setItem('taskTimers', JSON.stringify(state.timers));
      }
    },

    resumeTimer: (state, action) => {
      const { activityId } = action.payload;
      if (state.timers[activityId] && state.timers[activityId].pausedAt) {
        const now = Date.now();
        const pauseDuration = now - state.timers[activityId].pausedAt;
        state.timers[activityId].totalPausedDuration += pauseDuration;
        state.timers[activityId].timerPaused = false;
        state.timers[activityId].pausedAt = null;
        state.timers[activityId].lastUpdateTime = now;
        localStorage.setItem('taskTimers', JSON.stringify(state.timers));
      }
    },

    updateTimeRemaining: (state, action) => {
      const { activityId, timeRemainingSeconds } = action.payload;
      if (state.timers[activityId]) {
        state.timers[activityId].timeRemainingSeconds = timeRemainingSeconds;
        localStorage.setItem('taskTimers', JSON.stringify(state.timers));
      }
    },

    extendTimerByMinutes: (state, action) => {
      const { activityId, extraMinutes } = action.payload;
      const timer = state.timers[activityId];
      if (timer && extraMinutes > 0) {
        const extraSeconds = extraMinutes * 60;
        timer.estimatedMinutes = (timer.estimatedMinutes || 0) + extraMinutes;
        timer.totalSeconds = (timer.totalSeconds || 0) + extraSeconds;
        // If we already have a remaining time, bump it as well so UI updates immediately
        if (typeof timer.timeRemainingSeconds === 'number') {
          timer.timeRemainingSeconds += extraSeconds;
        }
        localStorage.setItem('taskTimers', JSON.stringify(state.timers));
      }
    },

    stopTimer: (state, action) => {
      const { activityId } = action.payload;
      delete state.timers[activityId];
      localStorage.setItem('taskTimers', JSON.stringify(state.timers));
    },

    clearAllTimers: (state) => {
      state.timers = {};
      localStorage.removeItem('taskTimers');
    },
  },
});

export const {
  startTimer,
  pauseTimer,
  resumeTimer,
  updateTimeRemaining,
  extendTimerByMinutes,
  stopTimer,
  clearAllTimers,
} = taskTimerSlice.actions;

export default taskTimerSlice.reducer;

// Helper function to calculate actual time remaining based on elapsed time
export const calculateTimeRemaining = (timer) => {
  if (!timer || !timer.timerActive) return null;

  const now = Date.now();
  const { startTime, totalSeconds, totalPausedDuration, timerPaused, pausedAt } = timer;

  // Calculate total elapsed time (excluding paused time)
  let elapsedTime = now - startTime - totalPausedDuration;

  // If currently paused, don't count the current pause duration
  if (timerPaused && pausedAt) {
    elapsedTime -= now - pausedAt;
  }

  // Convert to seconds
  const elapsedSeconds = Math.floor(elapsedTime / 1000);

  // Calculate remaining time
  const remaining = totalSeconds - elapsedSeconds;

  return Math.max(0, remaining);
};

// Selectors
export const selectTimerForActivity = (state, activityId) => state.taskTimer.timers[activityId];
export const selectAllTimers = (state) => state.taskTimer.timers;
